import { useState } from "react";
import type { ConnectedAccount, PlatformId, PlatformInfo, ScheduledPost } from "../api";
import { statusesFor, willPublish } from "../lib/platformStatus";
import { untilLabel, useNow } from "../lib/useNow";

/**
 * The "what's actually going on" strip across the top of the dashboard. Answers,
 * at a glance and without a page refresh: what publishes next, to which
 * platforms, when — and what will quietly NOT publish (a disconnected or
 * pending-audit target), which the old list hid.
 */
export function StatusBanner({
  scheduled,
  platforms,
  accounts,
  onCancel,
  onEdit,
}: {
  scheduled: ScheduledPost[];
  platforms: PlatformInfo[];
  accounts: ConnectedAccount[];
  /** Cancel the next post — routed through the app's single confirm-guarded
   * handler. Async: the click handler awaits it to surface a failed cancel. */
  onCancel?: (id: string) => Promise<void>;
  /** Reopen the next post in the composer to reschedule/edit it. */
  onEdit?: (post: ScheduledPost) => void;
}) {
  const now = useNow(1000);
  // Local error surface for the Cancel action, mirroring Calendar's
  // DayItemList.handleCancel — so a failed cancel from the banner isn't silent
  // even if the parent handler didn't already narrate it (#2).
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Nothing queued → nothing to narrate. The dashboard's other cards cover the
  // idle state; a banner here would just be noise.
  if (scheduled.length === 0) return null;

  const upcoming = [...scheduled].sort((a, b) => a.at - b.at);
  const next = upcoming[0]!;
  const statuses = statusesFor(next.platforms as PlatformId[], platforms, accounts);
  const going = statuses.filter(willPublish);
  const held = statuses.filter((s) => !willPublish(s));

  const when = new Date(next.at);
  const whenText = when.toLocaleString(undefined, {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    month: "short",
    day: "numeric",
  });

  const due = next.at - now <= 0;

  return (
    <div className={`status-banner${due ? " is-due" : ""}`} role="status">
      <div className="sb-main">
        <div className="sb-lead">
          <span className="sb-eyebrow">{due ? "Publishing now" : "Next post"}</span>
          <span className="sb-count">
            {scheduled.length} post{scheduled.length === 1 ? "" : "s"} queued
          </span>
        </div>

        <div className="sb-title">{next.draft.title || "Untitled post"}</div>

        <div className="sb-when">
          <strong>{untilLabel(next.at, now)}</strong>
          <span className="muted"> · {whenText}</span>
        </div>

        {going.length > 0 ? (
          <div className="sb-targets">
            Publishing to{" "}
            {going.map((s, i) => (
              <span key={s.id} className="sb-go">
                {s.name}
                {i < going.length - 1 ? ", " : ""}
              </span>
            ))}
          </div>
        ) : (
          <div className="sb-targets sb-none">
            ⚠ No connected platform will publish this — every target is held below.
          </div>
        )}

        {held.length > 0 && (
          <div className="sb-held">
            {held.map((s) => (
              <span key={s.id} className="sb-hold" title={s.note}>
                {s.outcome === "pending" ? "⏳" : "✕"} {s.note}
              </span>
            ))}
          </div>
        )}

        {/* Manage the next post inline — the calendar is the full view, but the
            most imminent post is the one worth touching from here. */}
        {(onEdit || onCancel) && (
          <div className="sb-actions">
            {onEdit && (
              <button type="button" className="ghost small" onClick={() => onEdit(next)}>
                Edit
              </button>
            )}
            {onCancel && (
              <button
                type="button"
                className="ghost small"
                onClick={async () => {
                  setCancelError(null);
                  try {
                    await onCancel(next.id);
                  } catch (err) {
                    setCancelError(err instanceof Error ? err.message : "Could not cancel.");
                  }
                }}
              >
                Cancel
              </button>
            )}
          </div>
        )}

        {cancelError && <div className="sb-error error">{cancelError}</div>}
      </div>
    </div>
  );
}
